import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Slide,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Add,
  ArrowBack,
  CameraAlt,
  Close,
  Pause,
  PlayArrow,
  Remove,
  Search
} from '@mui/icons-material';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import AnimatedCheckIcon from 'components/shared/toasts/AnimatedCheckIcon';
import StopMotionImage from 'components/play/StopMotionImage';
import { PLAY_EXERCISES, MY_PLAY_RESULTS, SUBMIT_SELF_REPORTED_RESULT } from 'api/graphql/play';
import { metricOf, formatDuration } from 'components/play/workoutEstimate';
import { muscleLabel } from 'components/play/constants';

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

// Métrica de REGISTRO del ejercicio: distancia si la mide, si no lo que diga metricOf.
const loggerMetricOf = (exercise) => {
  const types = exercise?.parameter_types || [];
  if (types.includes('DISTANCE')) return 'DISTANCE_KM';
  return metricOf(exercise); // 'TIME_SECONDS' | 'REPS' (misma lógica espejada del backend)
};

/**
 * OTRIX Play — registro manual de una marca en 3 momentos con transiciones suaves:
 * 1) elegir ejercicio (buscador), 2) capturar el valor según su métrica (stepper gigante de
 * reps con mantener-presionado, cronómetro integrado para tiempo, km para distancia) + la
 * FOTO de evidencia (el entrenador la califica), 3) celebración sobria con el check animado.
 */
function PlayResultLoggerModal({ open, onClose, initialExercise = null }) {
  const { t } = useTranslation(['play']);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const photoRef = useRef(null);
  const holdRef = useRef(null);

  const [step, setStep] = useState('exercise'); // exercise | value | done
  const [exercise, setExercise] = useState(null);
  const [search, setSearch] = useState('');
  const [reps, setReps] = useState(10);
  const [seconds, setSeconds] = useState(30);
  const [km, setKm] = useState('1');
  const [chronoRunning, setChronoRunning] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [savedValue, setSavedValue] = useState(null);

  const { data } = useQuery(PLAY_EXERCISES, { variables: {}, skip: !open, fetchPolicy: 'cache-and-network' });
  const [submitResult, { loading: saving }] = useMutation(SUBMIT_SELF_REPORTED_RESULT, {
    refetchQueries: [{ query: MY_PLAY_RESULTS, variables: {} }]
  });

  useEffect(() => {
    if (open) {
      setStep(initialExercise ? 'value' : 'exercise');
      setExercise(initialExercise || null);
      setSearch('');
      setReps(10);
      setSeconds(30);
      setKm('1');
      setChronoRunning(false);
      setPhoto(null);
      setSubmitted(false);
      setSavedValue(null);
      setPhotoPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    }
  }, [open, initialExercise]);

  // Cronómetro integrado (ejercicios por tiempo): cuenta hacia arriba de a 1s.
  useEffect(() => {
    if (!chronoRunning) return undefined;
    const timer = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, [chronoRunning]);

  useEffect(
    () => () => {
      if (holdRef.current) clearInterval(holdRef.current);
    },
    []
  );

  const metric = loggerMetricOf(exercise);

  const exercises = useMemo(() => {
    const all = data?.playExercises || [];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (ex) => (ex.name || '').toLowerCase().includes(q) || (ex.localized_name || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  // Stepper con mantener-presionado (acelera tras el primer tramo).
  const startHold = (delta) => {
    setReps((r) => Math.max(1, r + delta));
    holdRef.current = setInterval(() => setReps((r) => Math.max(1, r + delta)), 120);
  };
  const stopHold = () => {
    if (holdRef.current) clearInterval(holdRef.current);
    holdRef.current = null;
  };

  const currentValue = () => {
    if (metric === 'TIME_SECONDS') return seconds;
    if (metric === 'DISTANCE_KM') return Number(km) || 0;
    return reps;
  };

  const onPickPhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setPhoto(file);
    setPhotoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    const value = currentValue();
    if (!exercise || !photo || value <= 0) return;

    setChronoRunning(false);
    try {
      await submitResult({ variables: { exercise_id: exercise.id, value, metric, photo } });
      setSavedValue(value);
      setStep('done');
    } catch {
      // El errorHandler global ya muestra el toast del fallo.
    }
  };

  const valueLabel = () => {
    if (metric === 'TIME_SECONDS') return formatDuration(savedValue ?? seconds);
    if (metric === 'DISTANCE_KM') return `${savedValue ?? km} km`;
    return t('logger.repsValue', { ns: 'play', count: savedValue ?? reps, defaultValue: '{{count}} reps' });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      fullScreen={fullScreen}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          backgroundColor: 'surface.content',
          backgroundImage: 'none',
          border: '1px solid',
          borderColor: 'hairline.main'
        }
      }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'hairline.main' }}>
        {step === 'value' && !initialExercise && (
          <IconButton size="small" onClick={() => setStep('exercise')} aria-label={t('logger.back', { ns: 'play', defaultValue: 'Volver' })}>
            <ArrowBack fontSize="small" />
          </IconButton>
        )}
        <Typography variant="h6" fontWeight={800} sx={{ flex: 1, minWidth: 0 }} noWrap>
          {step === 'done'
            ? t('logger.doneTitle', { ns: 'play', defaultValue: 'Marca registrada' })
            : t('logger.title', { ns: 'play', defaultValue: 'Registrar marca' })}
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label={t('recovery.close', { ns: 'play' })} sx={{ color: 'text.secondary' }}>
          <Close fontSize="small" />
        </IconButton>
      </Stack>

      <DialogContent>
        {/* Momento 1: elegir ejercicio */}
        {step === 'exercise' && (
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <TextField
              size="small"
              autoFocus
              placeholder={t('browser.search', { ns: 'play', defaultValue: 'Buscar ejercicio' })}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
                  </InputAdornment>
                )
              }}
            />
            <Stack spacing={1} sx={{ maxHeight: 380, overflowY: 'auto' }}>
              {exercises.map((ex) => (
                <Stack
                  key={ex.id}
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setExercise(ex);
                    setStep('value');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setExercise(ex);
                      setStep('value');
                    }
                  }}
                  sx={{
                    p: 1,
                    borderRadius: '12px',
                    border: '1px solid',
                    borderColor: 'hairline.main',
                    backgroundColor: 'surface.raised',
                    cursor: 'pointer',
                    transition: 'background-color 200ms cubic-bezier(0.32,0.72,0,1)',
                    '&:hover': { backgroundColor: 'surface.hover' },
                    '&:active': { transform: 'scale(0.98)' }
                  }}>
                  <Box sx={{ width: 36, flex: 'none' }}>
                    <StopMotionImage frame1={ex.image_url_1} frame2={ex.image_url_2} alt={ex.name} height={52} playing={false} />
                  </Box>
                  <Stack spacing={0} sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" fontWeight={800} noWrap>
                      {ex.localized_name || ex.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                      {muscleLabel(t, ex.muscle_group)}
                    </Typography>
                  </Stack>
                </Stack>
              ))}
              {exercises.length === 0 && (
                <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center', py: 3 }}>
                  {t('browser.empty', { ns: 'play' })}
                </Typography>
              )}
            </Stack>
          </Stack>
        )}

        {/* Momento 2: capturar valor + foto */}
        {step === 'value' && exercise && (
          <Stack spacing={2.5} alignItems="center" sx={{ pt: 1 }}>
            <Stack spacing={0.25} alignItems="center">
              <Typography variant="subtitle1" fontWeight={800}>
                {exercise.localized_name || exercise.name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {muscleLabel(t, exercise.muscle_group)}
              </Typography>
            </Stack>

            {metric === 'REPS' && (
              <Stack direction="row" spacing={2} alignItems="center">
                <IconButton
                  aria-label={t('logger.minus', { ns: 'play', defaultValue: 'Menos' })}
                  onMouseDown={() => startHold(-1)}
                  onMouseUp={stopHold}
                  onMouseLeave={stopHold}
                  onTouchStart={() => startHold(-1)}
                  onTouchEnd={stopHold}
                  sx={{ border: '1px solid', borderColor: 'hairline.strong', '&:active': { transform: 'scale(0.94)' } }}>
                  <Remove />
                </IconButton>
                <Typography
                  sx={{ fontWeight: 800, fontSize: '4.5rem', lineHeight: 1, fontVariantNumeric: 'tabular-nums', minWidth: 130, textAlign: 'center' }}>
                  {reps}
                </Typography>
                <IconButton
                  aria-label={t('logger.plus', { ns: 'play', defaultValue: 'Más' })}
                  onMouseDown={() => startHold(1)}
                  onMouseUp={stopHold}
                  onMouseLeave={stopHold}
                  onTouchStart={() => startHold(1)}
                  onTouchEnd={stopHold}
                  sx={{
                    color: 'ink.main',
                    backgroundColor: 'gold.main',
                    '&:hover': { backgroundColor: 'gold.dark' },
                    '&:active': { transform: 'scale(0.94)' }
                  }}>
                  <Add />
                </IconButton>
              </Stack>
            )}

            {metric === 'TIME_SECONDS' && (
              <Stack spacing={1.5} alignItems="center">
                <Typography sx={{ fontWeight: 800, fontSize: '4rem', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                  {formatDuration(seconds)}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={chronoRunning ? <Pause /> : <PlayArrow />}
                    onClick={() => setChronoRunning((r) => !r)}
                    sx={{ borderColor: 'hairline.strong', color: 'text.primary', textTransform: 'none', fontWeight: 700 }}>
                    {chronoRunning
                      ? t('logger.chronoStop', { ns: 'play', defaultValue: 'Detener' })
                      : t('logger.chronoStart', { ns: 'play', defaultValue: 'Cronometrar' })}
                  </Button>
                  <TextField
                    size="small"
                    type="number"
                    label={t('logger.seconds', { ns: 'play', defaultValue: 'Segundos' })}
                    value={seconds}
                    onChange={(e) => setSeconds(Math.max(0, Number(e.target.value) || 0))}
                    inputProps={{ min: 0 }}
                    sx={{ width: 120 }}
                  />
                </Stack>
              </Stack>
            )}

            {metric === 'DISTANCE_KM' && (
              <TextField
                type="number"
                label={t('logger.distance', { ns: 'play', defaultValue: 'Distancia (km)' })}
                value={km}
                onChange={(e) => setKm(e.target.value)}
                inputProps={{ min: 0, step: 0.1 }}
                sx={{ width: 180, '& input': { fontSize: '1.6rem', fontWeight: 800, textAlign: 'center' } }}
              />
            )}

            {/* Foto de evidencia (el entrenador la revisa y califica). */}
            <input ref={photoRef} type="file" accept="image/*" capture="environment" hidden onChange={onPickPhoto} />
            <Stack
              role="button"
              tabIndex={0}
              onClick={() => photoRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  photoRef.current?.click();
                }
              }}
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{
                width: '100%',
                p: 1.5,
                borderRadius: '12px',
                border: '1px dashed',
                borderColor: submitted && !photo ? 'gold.main' : 'hairline.main',
                backgroundColor: 'surface.overlay',
                cursor: 'pointer',
                '&:active': { transform: 'scale(0.99)' }
              }}>
              {photoPreview ? (
                <Box component="img" src={photoPreview} alt="" sx={{ width: 44, height: 44, objectFit: 'cover', borderRadius: '10px', flex: 'none' }} />
              ) : (
                <CameraAlt sx={{ color: 'gold.main', flex: 'none' }} />
              )}
              <Stack spacing={0} sx={{ minWidth: 0 }}>
                <Typography variant="caption" fontWeight={800}>
                  {photo
                    ? t('logger.photoReady', { ns: 'play', defaultValue: 'Foto lista (toca para cambiarla)' })
                    : t('logger.photo', { ns: 'play', defaultValue: 'Foto de evidencia' })}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('logger.photoHint', { ns: 'play', defaultValue: 'Tu entrenador la revisa y califica tu marca.' })}
                </Typography>
              </Stack>
            </Stack>
            {submitted && !photo && (
              <Typography variant="caption" sx={{ color: 'gold.main' }}>
                {t('logger.photoRequired', { ns: 'play', defaultValue: 'La foto es obligatoria para validar la marca.' })}
              </Typography>
            )}

            <Button fullWidth onClick={handleSubmit} disabled={saving} sx={{ ...premiumButtonSx, py: 1.2 }}>
              {saving ? <CircularProgress size={18} color="inherit" /> : t('logger.save', { ns: 'play', defaultValue: 'Guardar marca' })}
            </Button>
          </Stack>
        )}

        {/* Momento 3: celebración sobria */}
        {step === 'done' && (
          <Stack spacing={2} alignItems="center" sx={{ py: 3, textAlign: 'center' }}>
            <Box sx={{ transform: 'scale(2.2)', py: 1.5 }}>
              <AnimatedCheckIcon />
            </Box>
            <Typography variant="h6" fontWeight={800}>
              {valueLabel()}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 300 }}>
              {t('logger.doneBody', { ns: 'play', defaultValue: 'Tu marca quedó registrada y pasa a revisión de tu entrenador.' })}
            </Typography>
            <Button fullWidth onClick={onClose} sx={{ ...premiumButtonSx, py: 1.1, maxWidth: 280 }}>
              {t('recovery.close', { ns: 'play' })}
            </Button>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

PlayResultLoggerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  /** Si viene, se salta el selector y registra directo sobre este ejercicio. */
  initialExercise: PropTypes.object
};

export default PlayResultLoggerModal;
